import { useMemo, useState } from "react"
import { EmptyState, ErrorState, MoneyText, SegmentedControl } from "procurement/design"
import { useGetBudgetDashboardHeatmapQuery } from "shared/redux/endpoints/procurementV2"
import { pctOf } from "./chartSetup"
import { CenterPicker, useCenterPicker } from "./CenterPicker"

const STATUSES = ["GREEN", "YELLOW", "RED", "NO_BUDGET", "NO_BUDGET_SPENDING"]

const LEGEND = [
  ["GREEN", "< 70%"],
  ["YELLOW", "70–90%"],
  ["RED", "> 90%"],
  ["NO_BUDGET", "No budget"],
  ["NO_BUDGET_SPENDING", "Spending without budget"],
]

const statusOf = (c) => {
  if (STATUSES.includes(c.heatmapStatus)) return c.heatmapStatus
  const budget = Number(c.totalBudget) || 0
  if (budget <= 0) return Number(c.usedAmount) > 0 ? "NO_BUDGET_SPENDING" : "NO_BUDGET"
  const pct = Number(c.usedPercentage) || 0
  if (pct > 90) return "RED"
  if (pct >= 70) return "YELLOW"
  return "GREEN"
}

const TOOLTIP_ROWS = [
  ["totalBudget", "Budget"],
  ["committed", "Committed"],
  ["unpaid", "Unpaid"],
  ["paid", "Paid"],
  ["usedAmount", "Used"],
  ["remaining", "Remaining"],
]

const CellTooltip = ({ hover }) => {
  if (!hover) return null
  const { cell, rect } = hover
  const below = rect.top < 220
  return (
    <div
      className="br-heat-tip"
      role="tooltip"
      style={{
        left: rect.left + rect.width / 2,
        top: below ? rect.bottom + 8 : rect.top - 8,
        transform: `translate(-50%, ${below ? "0" : "-100%"})`,
      }}
    >
      <div className="br-heat-tip-title">
        {cell.budgetCenterName} · {cell.categoryName}
      </div>
      <dl>
        {TOOLTIP_ROWS.map(([key, label]) => (
          <div key={key} className={key === "remaining" && Number(cell.remaining) < 0 ? "is-negative" : undefined}>
            <dt>{label}</dt>
            <dd>
              <MoneyText value={cell[key]} />
            </dd>
          </div>
        ))}
        <div>
          <dt>Used %</dt>
          <dd>{pctOf(cell.usedPercentage)}</dd>
        </div>
      </dl>
    </div>
  )
}

const BudgetHeatmap = ({ params }) => {
  const [sortBy, setSortBy] = useState("used")
  const [hover, setHover] = useState(null)
  // Dev has ~3k cells; one large page keeps the matrix from missing cells.
  const { data, isFetching, isError } = useGetBudgetDashboardHeatmapQuery({ ...params, size: 5000 })
  const items = useMemo(() => data?.items || [], [data])
  const { filtered, pickerProps } = useCenterPicker(items)

  const { centers, categories, cells } = useMemo(() => {
    const centerMap = new Map()
    const categoryMap = new Map()
    const byKey = new Map()
    filtered.forEach((c) => {
      const pct = Number(c.usedPercentage) || 0
      const center = centerMap.get(c.budgetCenterId) || { id: c.budgetCenterId, name: c.budgetCenterName, maxPct: 0 }
      center.maxPct = Math.max(center.maxPct, pct)
      centerMap.set(c.budgetCenterId, center)
      categoryMap.set(c.budgetCategoryId, c.categoryName)
      byKey.set(`${c.budgetCenterId}:${c.budgetCategoryId}`, { ...c, status: statusOf(c) })
    })
    const byName = (a, b) => String(a.name).localeCompare(String(b.name))
    return {
      centers: [...centerMap.values()].sort(sortBy === "used" ? (a, b) => b.maxPct - a.maxPct || byName(a, b) : byName),
      categories: [...categoryMap].sort((a, b) => String(a[1]).localeCompare(String(b[1]))),
      cells: byKey,
    }
  }, [filtered, sortBy])

  if (isError) return <ErrorState text="Couldn't load the heatmap." />
  if (isFetching) return <p className="pc-hint br-pad">Loading heatmap…</p>
  if (items.length === 0) return <EmptyState title="No budget cells for this period." />

  return (
    <>
      <CenterPicker {...pickerProps} />
      <div className="br-heatmap-bar">
        <div className="br-heatmap-legend">
          {LEGEND.map(([status, label]) => (
            <span key={status}>
              <i className={`br-heat br-heat--${status}`} />
              {label}
            </span>
          ))}
        </div>
        <SegmentedControl
          value={sortBy}
          onChange={setSortBy}
          options={[
            { value: "used", label: "Highest used % first" },
            { value: "name", label: "Center A–Z" },
          ]}
        />
      </div>
      <div className="br-heatmap-scroll" onScroll={() => setHover(null)}>
        <table className="br-heatmap">
          <thead>
            <tr>
              <th>Center</th>
              {categories.map(([id, name]) => (
                <th key={id} title={name}>
                  <span>{name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody onMouseLeave={() => setHover(null)}>
            {centers.map((center) => (
              <tr key={center.id}>
                <th scope="row">{center.name}</th>
                {categories.map(([categoryId]) => {
                  const c = cells.get(`${center.id}:${categoryId}`)
                  if (!c) return <td key={categoryId} className="br-heat br-heat--EMPTY" />
                  return (
                    <td
                      key={categoryId}
                      className={`br-heat br-heat--${c.status}`}
                      tabIndex={0}
                      onMouseEnter={(e) => setHover({ cell: c, rect: e.currentTarget.getBoundingClientRect() })}
                      onFocus={(e) => setHover({ cell: c, rect: e.currentTarget.getBoundingClientRect() })}
                      onBlur={() => setHover(null)}
                    >
                      {c.status === "NO_BUDGET" ? "—" : pctOf(c.usedPercentage)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CellTooltip hover={hover} />
    </>
  )
}

export default BudgetHeatmap
